/**
 * Life4Earth V2 — User Resource Registry Types
 *
 * A user resource is something a person CAN OFFER to projects:
 * skills, tools, funding, land access, expertise, etc.
 *
 * This is the supply side of the matching engine.
 * Project pre-flight items (PreflightItem) are the demand side.
 *
 * Design principles:
 *   - Mirror project resource taxonomy (same categories)
 *   - Separate state machine: available → reserved → committed → unavailable
 *   - Community-scoped sharing: resources are shared with specific communities
 *   - Emergency response support: flag resources for crisis deployment
 */

import type { ResourceCategory } from '../PreFlight/preflightTypes';
import { RESOURCE_CATEGORIES } from '../PreFlight/preflightTypes';

/* ------------------------------------------------------------------ */
/*  Re-export shared enums so consumers import from one place          */
/* ------------------------------------------------------------------ */

export { RESOURCE_CATEGORIES } from '../PreFlight/preflightTypes';
export type { ResourceCategory };

/* ------------------------------------------------------------------ */
/*  Legacy constants (deprecated — kept for CriteriaZone & matching)   */
/* ------------------------------------------------------------------ */

/** @deprecated Use shared_communities instead. Kept for CriteriaZone filter UI. */
export const RESOURCE_REACH = ['local', 'regional', 'national', 'international'] as const;
/** @deprecated */
export type ResourceReach = (typeof RESOURCE_REACH)[number];

/** @deprecated Use offer_type instead. */
export const AVAILABILITY_TYPES = ['ongoing', 'one_time', 'recurring', 'seasonal'] as const;
/** @deprecated */
export type AvailabilityType = (typeof AVAILABILITY_TYPES)[number];

/** @deprecated Removed from UI. Kept for backward compat. */
export const USER_RESOURCE_VISIBILITIES = ['community', 'public', 'private'] as const;
/** @deprecated */
export type UserResourceVisibility = (typeof USER_RESOURCE_VISIBILITIES)[number];

/* ------------------------------------------------------------------ */
/*  User Resource State Machine                                        */
/* ------------------------------------------------------------------ */

/**
 * Lifecycle state for user-registered resources.
 *
 *   - available:   Ready to be matched to projects. Visible to matching engine.
 *   - reserved:    Tentatively matched — awaiting confirmation from both parties.
 *   - committed:   Locked to a specific project. Not available for other matches.
 *   - unavailable: Temporarily withdrawn (vacation, seasonal, etc.). Hidden from matching.
 *   - retired:     Permanently removed. Soft-deleted.
 */
export const USER_RESOURCE_STATES = [
  'available',
  'reserved',
  'committed',
  'unavailable',
  'retired',
] as const;

export type UserResourceState = (typeof USER_RESOURCE_STATES)[number];

/** States visible to the matching engine. */
export const MATCHABLE_USER_STATES: readonly UserResourceState[] = ['available'];

/**
 * Valid state transitions for user resources.
 */
export const USER_RESOURCE_TRANSITIONS: Record<UserResourceState, readonly UserResourceState[]> = {
  available:   ['reserved', 'unavailable', 'retired'],
  reserved:    ['committed', 'available'],       // confirm or release
  committed:   ['available'],                     // project complete or released
  unavailable: ['available', 'retired'],          // re-activate or retire
  retired:     ['available'],                     // un-retire
};

/* ------------------------------------------------------------------ */
/*  Offer Type                                                         */
/* ------------------------------------------------------------------ */

/**
 * How the resource is offered:
 *   - loan:     Borrower returns it after use
 *   - donation: One-time transfer, consumable
 *   - either:   Flexible — loan or donation
 */
export const OFFER_TYPES = [
  'loan',
  'donation',
  'either',
] as const;

export type OfferType = (typeof OFFER_TYPES)[number];

/* ------------------------------------------------------------------ */
/*  Review Decision                                                    */
/* ------------------------------------------------------------------ */

/**
 * Outcome of a project manager reviewing a resource offer:
 *   - accepted:  Resource is now committed to the project
 *   - rejected:  Resource returns to available
 *   - expired:   Offer timed out without review
 */
export const REVIEW_DECISIONS = ['accepted', 'rejected', 'expired'] as const;
export type ReviewDecision = (typeof REVIEW_DECISIONS)[number];

/* ------------------------------------------------------------------ */
/*  Offer Expiration Presets                                           */
/* ------------------------------------------------------------------ */

/** Days the offerer can choose for offer validity. */
export const OFFER_EXPIRATION_PRESETS = [7, 14, 30] as const;
export type OfferExpirationDays = (typeof OFFER_EXPIRATION_PRESETS)[number];

/** Default expiration if the user doesn't choose. */
export const DEFAULT_OFFER_EXPIRATION_DAYS: OfferExpirationDays = 14;

/* ------------------------------------------------------------------ */
/*  Resource Photo                                                     */
/* ------------------------------------------------------------------ */

export interface ResourcePhoto {
  url: string;
  sort_order: number;
}

/* ------------------------------------------------------------------ */
/*  User Resource                                                      */
/* ------------------------------------------------------------------ */

export interface UserResource {
  /** Unique identifier for this resource registration. */
  resource_guid: string;

  /** The user who registered this resource. */
  user_guid: string;

  /** Top-level category (same taxonomy as project resources). */
  category: ResourceCategory;

  /** Free-text label (e.g., "Carpentry skills", "John Deere tractor", "$500 donation"). */
  label: string;

  /** Optional longer description of what's being offered. */
  description?: string;

  /** Current lifecycle state. */
  state: UserResourceState;

  /** How this resource is offered: loan, donation, or either. */
  offer_type: OfferType;

  /**
   * Community entity GUIDs this resource is shared with.
   * Replaces reach + visibility — user picks specific communities.
   */
  shared_communities: string[];

  /** Up to 3 photos with ordering. First photo = card thumbnail. */
  photos: ResourcePhoto[];

  /** Whether this resource can be deployed in emergencies. */
  emergency_available: boolean;

  /** Whether this resource is restricted to emergency use only. */
  emergency_only: boolean;

  /** Numeric value for how long it takes to mobilize this resource. */
  deployment_time_minutes?: number | null;

  /** Semantic unit for deployment_time_minutes: 'minutes' | 'hours' | 'days'. */
  deployment_time_unit?: string | null;

  /** Free-form notes for emergency deployment (location, access, contact info). */
  emergency_notes?: string | null;

  /** License or registration number (certifications category only). */
  certification_number?: string | null;

  /** Date the certification was issued (ISO date string). */
  certification_date?: string | null;

  /** Date the certification expires (ISO date string). Null if it doesn't expire. */
  certification_expiry?: string | null;

  /** Optional conditions or notes for lending (free-text). */
  conditions?: string | null;

  /** GUID of the project this resource is currently committed to (when state = committed). */
  committed_project_guid?: string | null;

  /* ── Offer Handshake (migration 112) ── */

  /** ISO timestamp when the offer was made (state → reserved). */
  offered_at?: string | null;

  /** ISO timestamp when the offer expires. Null if no active offer. */
  offer_expires_at?: string | null;

  /** FK to preflight_items: which specific resource need this offer addresses. */
  offered_item_guid?: string | null;

  /** Free-text message from the offerer to the project manager. */
  offer_notes?: string | null;

  /** User GUID of the reviewer (project manager) who accepted/rejected. */
  reviewed_by_guid?: string | null;

  /** ISO timestamp when the review decision was made. */
  reviewed_at?: string | null;

  /** Outcome: 'accepted' | 'rejected' | 'expired'. */
  review_decision?: ReviewDecision | null;

  /** Optional message from reviewer back to offerer. */
  review_note?: string | null;

  /** ISO timestamp of creation. */
  created_at: string;

  /** ISO timestamp of last update. */
  updated_at: string;

  /** Display order in the user's registry. */
  sort_order: number;

  /* ── Legacy fields (kept for backward compat during migration) ── */

  /** @deprecated Use offer_type instead. */
  availability_type?: string;

  /** @deprecated Use shared_communities instead. */
  reach?: string;

  /** @deprecated Use shared_communities instead. */
  visibility?: string;

  /** @deprecated No longer used. */
  descriptor_tags?: string[];

  /** @deprecated Quantity is described in the description text. */
  quantity?: number | null;

  /** @deprecated Unit is described in the description text. */
  unit?: string | null;

  /** @deprecated Use shared_communities instead. */
  location_entity_guid?: string | null;
}

/* ------------------------------------------------------------------ */
/*  Summary                                                            */
/* ------------------------------------------------------------------ */

export interface UserResourceSummary {
  total: number;
  available: number;
  reserved: number;
  committed: number;
  unavailable: number;
  retired: number;
  by_category: Record<ResourceCategory, number>;
}

export function computeUserResourceSummary(resources: UserResource[]): UserResourceSummary {
  const summary: UserResourceSummary = {
    total: resources.length,
    available: 0,
    reserved: 0,
    committed: 0,
    unavailable: 0,
    retired: 0,
    by_category: {} as Record<ResourceCategory, number>,
  };

  for (const cat of RESOURCE_CATEGORIES) {
    summary.by_category[cat] = 0;
  }

  for (const r of resources) {
    summary[r.state]++;
    if (r.category in summary.by_category) {
      summary.by_category[r.category]++;
    }
  }

  return summary;
}

/* ------------------------------------------------------------------ */
/*  Validation helpers                                                 */
/* ------------------------------------------------------------------ */

export function isValidUserResourceTransition(from: UserResourceState, to: UserResourceState): boolean {
  return (USER_RESOURCE_TRANSITIONS[from] as readonly string[]).includes(to);
}
